import { apiUrl } from '../../api/client';
import { getDistributorClient } from '../../api/distributorClient';
import { getDistributorToken, redirectToManage } from '../../auth/useDistributorAuth';
import type { DistributorUser } from '../../auth/useDistributorAuth';
import type { components, operations } from '../../api/schema';

export class ExistingClientError extends Error {
  constructor() {
    super('Client is already using Real Time Feedback');
  }
}

export type TokenExchangeResponse = {
  token: string;
  redirect_to: string | null;
  business_id: number | null;
  user_type: string | null;
  distributor: DistributorUser | null;
};

async function distributorFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getDistributorToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: { ...headers, ...(options?.headers as Record<string, string>) },
  });
  if (response.status === 401) {
    redirectToManage();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    if (path === '/d/dashboard/reports' && response.status === 409) {
      throw new ExistingClientError();
    }
    const text = await response.text();
    throw new Error(`API error ${response.status}: ${text}`);
  }
  return response.json() as Promise<T>;
}

export async function exchangeToken(
  code: string,
  state: string,
): Promise<TokenExchangeResponse> {
  const data = await fetch(`${apiUrl}/d/dashboard/auth/token-exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });
  if (!data.ok) {
    const text = await data.text();
    throw new Error(`Token exchange failed: ${text}`);
  }
  return data.json();
}

export type DistributorMeResponse = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  company: string | null;
  additional_info: string | null;
  report_count: number;
};

export async function getDistributorMe(): Promise<DistributorMeResponse> {
  return distributorFetch<DistributorMeResponse>('/d/dashboard/me');
}

export async function getDashboardHierarchy() {
  const { data, error } = await getDistributorClient().GET('/d/dashboard/hierarchy');
  if (error) throw new Error('Unable to load your downline. Try again.');
  return data;
}

export async function updateDashboardNotification(descendantUuid: string, enabled: boolean) {
  const { data, error } = await getDistributorClient().PUT(
    '/d/dashboard/notifications/{descendant_uuid}',
    { params: { path: { descendant_uuid: descendantUuid } }, body: { enabled } },
  );
  if (error || !data) throw new Error('Unable to save email preferences. Try again.');
  return data;
}

export type DistributorReport = components['schemas']['DistributorReportHistoryItem'];
export type DistributorReportsResponse = components['schemas']['DistributorReportHistoryResponse'];
export type DistributorReportsQuery = NonNullable<
  operations['get_reports_d_dashboard_reports_get']['parameters']['query']
>;

export async function getDistributorReports(
  query: DistributorReportsQuery = {},
): Promise<DistributorReportsResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  return distributorFetch<DistributorReportsResponse>(
    `/d/dashboard/reports?${params.toString()}`,
  );
}

export async function getDistributorReport(
  uuid: string,
): Promise<DistributorReport> {
  return distributorFetch<DistributorReport>(
    `/d/dashboard/reports/${encodeURIComponent(uuid)}`,
  );
}

export type CreateDistributorReportRequest = {
  place_id: string;
  contact?: {
    first_name?: string | null;
    last_name?: string | null;
    phone_number?: string | null;
    email?: string | null;
    title?: string | null;
  };
  business_type?: string;
};

export type CreateDistributorReportResponse = {
  uuid: string;
};

export async function createDistributorReport(
  body: CreateDistributorReportRequest,
): Promise<CreateDistributorReportResponse> {
  return distributorFetch<CreateDistributorReportResponse>('/d/dashboard/reports', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export type DistributorContact = {
  uuid: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  title: string | null;
  created_at: string;
};

export async function getDistributorContacts(
  limit = 50,
  offset = 0,
): Promise<DistributorContact[]> {
  return distributorFetch<DistributorContact[]>(
    `/d/dashboard/contacts?limit=${limit}&offset=${offset}`,
  );
}
